import {mergeAttributes, Node} from '@tiptap/core';
import {ReactNodeViewRenderer} from '@tiptap/react';

import {MeaInfoComponent} from './mea-info-component';
import {MeaInfoEnum} from './mea-info-types';

export interface InfoBlockOptions {
  types: Record<string, string>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    infoBlock: {
      setMeaInfoBlock: (type: MeaInfoEnum) => ReturnType;
      unsetMeaInfoBlock: () => ReturnType;
    };
  }
}

export const MeaInfo = Node.create<InfoBlockOptions>({
  name: 'meaInfo',

  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      type: {
        default: MeaInfoEnum.default,
        parseHTML: (element) => element.getAttribute('data-mea-type') || MeaInfoEnum.default,
        renderHTML: (attributes) => ({
          'data-mea-type': attributes.type,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-mea-type]',
      },
    ];
  },

  renderHTML({HTMLAttributes}) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: `mea-info ${HTMLAttributes['data-mea-type']}`,
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MeaInfoComponent);
  },

  addCommands() {
    return {
      setMeaInfoBlock:
        (type: MeaInfoEnum) =>
        ({state, tr, dispatch}) => {
          const {$from, $to} = state.selection;
          const paragraphType = state.schema.nodes.paragraph;
          const meaInfoType = state.schema.nodes.meaInfo;

          if (!paragraphType || !meaInfoType) return false;

          const content: any[] = [];

          state.doc.nodesBetween($from.pos, $to.pos, (node) => {
            if (node.type === paragraphType) {
              content.push(paragraphType.create({}, node.content));
            }
          });

          if (content.length === 0) return false;

          const infoBlock = meaInfoType.create({type}, content);

          tr.replaceRangeWith($from.before(), $to.after(), infoBlock);

          dispatch?.(tr);
          return true;
        },

      unsetMeaInfoBlock:
        () =>
        ({state, tr, dispatch}) => {
          const {$from} = state.selection;
          const parent = $from.node($from.depth);

          if (parent.type.name !== 'meaInfo') return false;

          const paragraphType = state.schema.nodes.paragraph;
          const newParagraph = paragraphType.create({}, parent.content);

          tr.replaceRangeWith($from.before(), $from.after(), newParagraph);

          dispatch?.(tr);
          return true;
        },
    };
  },
});
